import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LanguageSwitcher from "@/components/site/LanguageSwitcher";
import { BUSINESS_CATEGORIES } from "@/lib/business";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Loader2, Check, Store, Pencil, Globe, SkipForward } from "lucide-react";

// Chat flow steps — ordered question list
const FLOW = [
    { key: "language",   kind: "language" },
    { key: "business_name", kind: "text",   q: "onb_ask_name", placeholder: "onb_name_placeholder" },
    { key: "category",      kind: "chips_cat", q: "onb_ask_category" },
    { key: "has_products",  kind: "choice", q: "onb_ask_type", options: [
        { v: true,  label: "onb_type_products" },
        { v: false, label: "onb_type_services" },
    ]},
    { key: "offerings",     kind: "chips_offerings", q: "onb_ask_offerings" },
    { key: "city",          kind: "text", q: "onb_ask_city", placeholder: null },
    { key: "area",          kind: "text", q: "onb_ask_area", placeholder: null, optional: true },
    { key: "address",       kind: "text", q: "onb_ask_address", placeholder: null, optional: true },
    { key: "about",         kind: "chips_about", q: "onb_ask_about" },
    { key: "special",       kind: "chips_special", q: "onb_ask_special", optional: true },
    { key: "phone",         kind: "text", q: "onb_ask_phone", placeholder: "+91 90000 00000" },
    { key: "whatsapp",      kind: "text", q: "onb_ask_whatsapp", placeholder: "919000000000", optional: true },
    { key: "email",         kind: "text", q: "onb_ask_email", placeholder: "you@example.com", optional: true },
    { key: "generate",      kind: "generate" },
];

export default function OnboardingPage() {
    const navigate = useNavigate();
    const { user } = useApp();
    const { t, lang, templates } = useI18n();

    const [stepIdx, setStepIdx] = useState(0);
    const [answers, setAnswers] = useState({
        business_name: "", category: "", offerings: "", has_products: true,
        city: "", area: "", address: "", about: "", special: "",
        phone: "", whatsapp: "", email: user?.email || "",
    });
    const [draft, setDraft] = useState("");
    const [writeMode, setWriteMode] = useState(false);
    const [generating, setGenerating] = useState(false);

    // Chat history: [{role, kind?, content, stepKey?}]
    const [history, setHistory] = useState([]);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    const step = FLOW[stepIdx];

    // Seed first bot message on mount
    useEffect(() => {
        setHistory([{ role: "bot", content: t("onb_bot_hello") }]);
    }, []); // eslint-disable-line

    // When step changes, push the bot's question
    useEffect(() => {
        if (!step) return;
        if (step.kind === "generate" || step.kind === "language") return;
        setWriteMode(false);
        setDraft(step.key === "whatsapp" || step.key === "phone" ? "+91 " : "");
        setHistory((h) => {
            const last = h[h.length - 1];
            const q = t(step.q);
            if (last?.role === "bot" && last.content === q) return h;
            return [...h, { role: "bot", content: q, stepKey: step.key }];
        });
    }, [stepIdx, lang]); // eslint-disable-line

    // Auto-scroll to latest message after each answer / new question
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const id = setTimeout(() => {
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
        }, 60);
        return () => clearTimeout(id);
    }, [history, stepIdx, writeMode, generating]);

    useEffect(() => {
        if (step?.kind === "text" && writeMode && inputRef.current) {
            inputRef.current.focus();
        }
    }, [step, writeMode]);

    const pushUser = (label, raw) => {
        setHistory((h) => [...h, { role: "user", content: label, raw }]);
    };

    const commit = (key, value, display) => {
        setAnswers((a) => ({ ...a, [key]: value }));
        pushUser(display ?? String(value), value);
        setStepIdx((i) => Math.min(i + 1, FLOW.length - 1));
    };

    const skipStep = () => {
        pushUser(t("onb_skip"), "");
        setStepIdx((i) => Math.min(i + 1, FLOW.length - 1));
    };

    // Pre-made templates per step
    const chipsFor = (key) => {
        if (key === "offerings") {
            return answers.has_products
                ? (templates.offerings_products || [])
                : (templates.offerings_services || []);
        }
        if (key === "about") return templates.about_hints || [];
        if (key === "special") return templates.special_hints || [];
        return [];
    };

    // ---- Generate ----
    useEffect(() => {
        if (step?.kind === "generate" && !generating) finish();
        // eslint-disable-next-line
    }, [step?.kind]);

    const finish = async () => {
        setGenerating(true);
        try {
            let biz = (await api.get("/my/business")).data;
            if (!biz) {
                biz = (await api.post("/my/business", { name: answers.business_name, category: answers.category })).data;
            }
            const ai = (await api.post("/ai/generate", { ...answers })).data;
            const products = answers.has_products
                ? (ai.product_categories || []).map((name) => ({ name, image_url: "", description: "", category: "", available: true }))
                : [];
            const services = (ai.services || []).map((name) => ({ name, image_url: "", description: "" }));
            await api.put(`/my/business/${biz.id}`, {
                name: answers.business_name, category: answers.category,
                tagline: ai.tagline || "", about: ai.about || answers.about, description: ai.description || "",
                products, services, why_choose_us: ai.why_choose_us || [],
                seo_title: ai.seo_title || "", seo_description: ai.seo_description || "",
                phone: answers.phone, whatsapp: answers.whatsapp || answers.phone, email: answers.email,
                city: answers.city, area: answers.area, address: answers.address,
            });
            toast.success(t("onb_done"));
            navigate("/dashboard?ready=1");
        } catch (err) {
            toast.error(formatApiError(err));
            setGenerating(false);
        }
    };

    const progress = Math.min(100, Math.round(((stepIdx + 1) / FLOW.length) * 100));

    return (
        <div className="relative min-h-screen flex flex-col">
            {/* Top bar */}
            <header className="sticky top-0 z-30 glass border-b border-border/60">
                <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full sphere" />
                        <span className="font-heading text-sm font-semibold uppercase tracking-[0.22em]">Bizlinkk</span>
                    </div>
                    <LanguageSwitcher variant="ghost" testid="onb-lang" />
                </div>
                <div className="h-1 bg-border/40">
                    <div className="h-full bg-accent transition-all duration-500" style={{ width: `${progress}%` }} data-testid="onb-progress" />
                </div>
            </header>

            {/* Chat body */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-6">
                <div className="max-w-2xl mx-auto space-y-3" data-testid="onb-chat">
                    {history.map((m, i) => (
                        <ChatBubble key={i} role={m.role} testid={`msg-${i}`}>{m.content}</ChatBubble>
                    ))}
                    {step?.kind === "generate" && (
                        <div className="text-center py-10 fade-up" data-testid="onb-generating-card">
                            {generating ? (
                                <>
                                    <Loader2 className="w-10 h-10 text-accent animate-spin mx-auto" />
                                    <h2 className="font-heading text-xl font-semibold mt-5">{t("onb_generating")}</h2>
                                    <p className="mt-2 text-sm text-muted-foreground">{t("onb_generating_sub")}</p>
                                </>
                            ) : (
                                <>
                                    <div className="w-14 h-14 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-emerald-600" /></div>
                                    <h2 className="font-heading text-xl font-semibold mt-5">{t("onb_done")}</h2>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Composer */}
            {step && step.kind !== "generate" && (
                <div className="sticky bottom-0 z-30 border-t border-border/60 bg-background/80 backdrop-blur px-4 py-3">
                    <div className="max-w-2xl mx-auto">
                        {step.kind === "language" && <LanguageStep onDone={() => setStepIdx(1)} />}

                        {step.kind === "text" && (
                            <TextComposer
                                inputRef={inputRef}
                                value={draft}
                                onChange={setDraft}
                                placeholder={step.placeholder ? t(step.placeholder) : t("onb_write_placeholder")}
                                onSubmit={() => {
                                    const v = draft.trim();
                                    if (!v && !step.optional) return;
                                    commit(step.key, v, v || t("onb_skip"));
                                }}
                                allowSkip={!!step.optional}
                                onSkip={skipStep}
                                t={t}
                            />
                        )}

                        {step.kind === "choice" && (
                            <div className="flex flex-wrap gap-2">
                                {step.options.map((opt) => (
                                    <ChipBtn key={String(opt.v)} onClick={() => commit(step.key, opt.v, t(opt.label))} testid={`choice-${step.key}-${opt.v}`}>
                                        {t(opt.label)}
                                    </ChipBtn>
                                ))}
                            </div>
                        )}

                        {step.kind === "chips_cat" && (
                            <div className="flex flex-wrap gap-2 max-h-[200px] overflow-y-auto no-scrollbar">
                                {BUSINESS_CATEGORIES.map((c) => (
                                    <ChipBtn key={c} onClick={() => commit("category", c, c)} testid={`cat-${c}`}>{c}</ChipBtn>
                                ))}
                            </div>
                        )}

                        {(step.kind === "chips_offerings" || step.kind === "chips_about" || step.kind === "chips_special") && (
                            writeMode ? (
                                <TextComposer
                                    inputRef={inputRef}
                                    value={draft}
                                    onChange={setDraft}
                                    placeholder={t("onb_write_placeholder")}
                                    onSubmit={() => {
                                        const v = draft.trim();
                                        if (!v && !step.optional) return;
                                        commit(step.key, v, v || t("onb_skip"));
                                    }}
                                    allowSkip={!!step.optional}
                                    onSkip={skipStep}
                                    t={t}
                                />
                            ) : (
                                <div>
                                    <div className="flex flex-wrap gap-2">
                                        {chipsFor(step.key).map((text) => (
                                            <ChipBtn key={text} onClick={() => commit(step.key, text, text)} testid={`tpl-${step.key}`}>{text}</ChipBtn>
                                        ))}
                                        <ChipBtn
                                            onClick={() => setWriteMode(true)}
                                            variant="outline"
                                            testid={`write-own-${step.key}`}
                                        >
                                            <Pencil className="w-3.5 h-3.5" /> {t("write_my_own")}
                                        </ChipBtn>
                                        {step.optional && (
                                            <ChipBtn onClick={skipStep} variant="ghost" testid={`skip-${step.key}`}>
                                                <SkipForward className="w-3.5 h-3.5" /> {t("onb_skip")}
                                            </ChipBtn>
                                        )}
                                    </div>
                                </div>
                            )
                        )}
                    </div>
                </div>
            )}

            <p className="text-center text-xs text-muted-foreground py-3 flex items-center justify-center gap-1.5">
                <Store className="w-3.5 h-3.5" /> {t("onb_edit_note")}
            </p>
        </div>
    );
}

function ChatBubble({ role, children, testid }) {
    const isUser = role === "user";
    return (
        <div className={`flex ${isUser ? "justify-end" : "justify-start"} fade-up`}>
            {!isUser && <div className="w-7 h-7 rounded-full sphere shrink-0 mt-0.5 mr-2" />}
            <div
                data-testid={testid}
                className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    isUser
                        ? "bg-accent text-white rounded-br-md"
                        : "glass text-foreground rounded-tl-md"
                }`}
            >
                {children}
            </div>
        </div>
    );
}

function ChipBtn({ children, onClick, variant = "default", testid }) {
    const cls =
        variant === "outline"
            ? "border border-accent/40 text-accent hover:bg-accent/10"
            : variant === "ghost"
            ? "text-muted-foreground hover:text-foreground"
            : "glass hover:text-accent";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm transition-all active:scale-95 ${cls}`}
        >
            {children}
        </button>
    );
}

function TextComposer({ inputRef, value, onChange, placeholder, onSubmit, allowSkip, onSkip, t }) {
    return (
        <form
            onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
            className="flex items-center gap-2"
            data-testid="onb-composer"
        >
            <Input
                ref={inputRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="rounded-full h-11"
                data-testid="onb-text-input"
            />
            {allowSkip && (
                <Button
                    type="button"
                    variant="ghost"
                    onClick={onSkip}
                    className="rounded-full h-11"
                    data-testid="onb-skip-btn"
                >
                    {t("onb_skip")}
                </Button>
            )}
            <Button
                type="submit"
                className="btn-accent rounded-full h-11 px-5 shrink-0"
                data-testid="onb-send-btn"
            >
                <ArrowRight className="w-4 h-4" />
            </Button>
        </form>
    );
}

function LanguageStep({ onDone }) {
    const { t, languages, lang, setLang } = useI18n();
    return (
        <div className="fade-up">
            <div className="mb-3 flex items-center gap-2 text-sm text-muted-foreground">
                <Globe className="w-4 h-4 text-accent" /> {t("choose_language")}
            </div>
            <p className="text-xs text-muted-foreground mb-3">{t("choose_language_sub")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {languages.map((l) => (
                    <button
                        key={l.code}
                        onClick={() => { setLang(l.code); }}
                        className={`rounded-xl px-3 py-3 text-left border transition-all ${
                            lang === l.code
                                ? "bg-foreground text-background border-foreground"
                                : "glass border-transparent hover:text-accent"
                        }`}
                        data-testid={`onb-lang-opt-${l.code}`}
                    >
                        <div className="text-sm font-medium leading-tight">{l.native}</div>
                        <div className="text-[11px] opacity-70 mt-0.5">{l.name}</div>
                    </button>
                ))}
            </div>
            <div className="mt-3 flex justify-end">
                <Button onClick={onDone} className="btn-accent rounded-full" data-testid="onb-lang-continue">
                    {t("continue")} <ArrowRight className="ml-1 w-4 h-4" />
                </Button>
            </div>
        </div>
    );
}
